import {useEffect, useRef, useState} from 'react';
import {BaSeButton, BaSeIcon} from '@base/react';
import lightbulb from '../../assets/images/icons/light.svg';
import ModalDefault from '../modals/modal-default';
import ApiShare from '../../services/share';
import {toast} from 'react-toastify';
import ToastContent from '../toastContent';
import {eventTag} from "../../services/utils";
import './index.scss';

const apiShare = ApiShare();

const ShareDetailsPopup = ({ users, onClose, modal = false, insidePlanning = false, toggleRef, isPlanner, onUserRemoved, event, postCredit = false }) => {
  const [activeRoleIndex, setActiveRoleIndex] = useState(null);
  const [openOrderPopup, setOpenOrderPopup] = useState(false);
  const [sortOrder, setSortOrder] = useState('none');
  const [rolePopupPosition, setRolePopupPosition] = useState({ top: 0, left: 0, bottom: 'unset' });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const popupRef = useRef(null);
  const rolePopupRef = useRef(null);
  const orderPopupRef = useRef(null);
  const orderButtonRef = useRef(null);
  const roleTriggerRefs = useRef([]);

  const roleMap = {
    'Visualização': 'visualizador',
    'Edição': 'editor',
    'Consultor': 'consultor',
  };

  const getRoleIcon = (role) => {
    return role === 'Visualização' ? 'eye' : 'edit-alt';
  };

  const handleRoleClick = (index) => {
    if (!isPlanner) return;
    
    if (activeRoleIndex === index) {
      setActiveRoleIndex(null);
      return;
    }

    const triggerElement = roleTriggerRefs.current[index];
    if (triggerElement) {
      const rect = triggerElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const shouldOpenUpward = rect.bottom > viewportHeight * 0.7;

      let leftPosition = rect.left;
      if (viewportWidth < 512) {
        leftPosition = rect.left - 20;
      }

      if (shouldOpenUpward) {
        setRolePopupPosition({
          top: 'unset',
          bottom: viewportHeight - rect.top + 4,
          left: leftPosition
        });
      } else {
        setRolePopupPosition({
          top: rect.bottom + 4,
          bottom: 'unset',
          left: leftPosition
        });
      }
    }
    setActiveRoleIndex(index);
  };
  
  const handleChangeRole = (newRole, index) => {
    const backendRole = roleMap[newRole];
    const data = { role: backendRole, status: 'aceito' };

    const shareId = users[index].shareId;

    if (!shareId) {
      toast.error(
        <ToastContent
          description="Erro ao identificar o usuário. Tente novamente."
          title="Erro!"
        />
      );
      return;
    }

    // setLoading(true);
    const changeStatusRequest = postCredit ? apiShare.changeStatusPostCredit : apiShare.changeStatus;

    changeStatusRequest(shareId, data)
      .then((res) => {
        users[index].role = newRole;
        toast.success(
          <ToastContent
            description="Permissão alterada com sucesso."
            title="Sucesso!"
          />
        );
        eventTag(event?.name, "atendimento", "submit", `Permissão alterada: ${newRole}`);
      })
      .catch(() => {
        toast.error(
          <ToastContent
            description="Não foi possível alterar a permissão. Tente novamente mais tarde."
            title="Erro!"
          />
        );
      })
      .finally(() => {
        // setLoading(false);
        setActiveRoleIndex(null);
      });
  };

  const handleRemoveUser = (shareId) => {
    if (!userToRemove) return;

    const changeStatusRequest = postCredit ? apiShare.changeStatusPostCredit : apiShare.changeStatus;

    changeStatusRequest(shareId, { status: 'removido' })
      .then(() => {        
        toast.success(
          <ToastContent
            description="Usuário removido com sucesso."
            title="Sucesso!"
          />
        );
        eventTag(event?.name, "atendimento", "submit", "Remoção de permissão");
        if (onUserRemoved) {
          onUserRemoved(userToRemove.shareId);
        }
      })
      .catch(() => {
        toast.error(
          <ToastContent
            description="Não foi possível remover o usuário. Tente novamente mais tarde."
            title="Erro!"
          />
        );
      })
      .finally(() => {
        setShowConfirmationModal(false);
        setUserToRemove(null);
      });
  };

  const getSortedUsers = () => {
    if (sortOrder === 'none') return users;
    
    const usersCopy = [...users];
    
    if (sortOrder === 'name-asc') {
      return usersCopy.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    if (sortOrder === 'name-desc') {
      return usersCopy.sort((a, b) => b.name.localeCompare(a.name));
    }
    
    if (sortOrder === 'status-view') {
      return usersCopy.sort((a, b) => {
        if (a.role === 'Visualização' && b.role !== 'Visualização') return -1;
        if (a.role !== 'Visualização' && b.role === 'Visualização') return 1;
        return 0;
      });
    }
    
    if (sortOrder === 'status-edit') {
      return usersCopy.sort((a, b) => {
        if (a.role === 'Edição' && b.role !== 'Edição') return -1;
        if (a.role !== 'Edição' && b.role === 'Edição') return 1;
        return 0;
      });
    }
    
    return usersCopy;
  };

  const isMobile = window.innerWidth < 768;
  const styles = insidePlanning
    ? {
        top: isMobile ? '-140px' : '40px',
        left: isMobile ? '0px' : '-180px',
      }
    : {
        top: 'calc(100% + 8px)',
        right: modal ? 'unset' : '-22px',
      };

  const ConfirmationModalBody = () => (
    <div className="confirmation-modal-body">
      <p>Você está prestes a remover o acesso deste usuário. Ele não poderá entrar na área de Planejamento, visualizar ou editar informações.</p>
      <div className='wrapper-buttons'>
        <BaSeButton
          onClick={() => {
            setShowConfirmationModal(false);
            setUserToRemove(null);
          }}
          type="secondary"
          value="Cancelar"
          color="#2E5DE6"
        />
        <BaSeButton
          onClick={() => {
            handleRemoveUser(userToRemove.shareId)
            eventTag(event?.name, "atendimento", event?.action, "Confirmar: Remover acesso")
          }}
          type="primary"
          value="Remover acesso"
          color="#2E5DE6"
        />
      </div>
    </div>
  )

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedRoleTrigger = roleTriggerRefs.current.some(
        ref => ref?.contains(event.target)
      );
      
      if (rolePopupRef.current && !rolePopupRef.current.contains(event.target) && !clickedRoleTrigger) {
        setActiveRoleIndex(null);
      }

      const clickedOrderButton = orderButtonRef.current?.contains(event.target);
      
      if (orderPopupRef.current && !orderPopupRef.current.contains(event.target) && !clickedOrderButton) {
        setOpenOrderPopup(false);
      }
      
      const clickedToggle = toggleRef?.current?.contains(event.target);
      
      if (popupRef.current && !popupRef.current.contains(event.target) && !clickedToggle) {
        onClose();
      }
    };

    const handleScroll = () => {
      if (activeRoleIndex !== null) {
        setActiveRoleIndex(null);
      }
      if (openOrderPopup) {
        setOpenOrderPopup(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [onClose, activeRoleIndex, openOrderPopup, toggleRef]);

  const sortedUsers = getSortedUsers();

  return (
    <div
      ref={popupRef}
      className={`share-details-popup ${modal ? 'modal-style' : ''}`}
      style={styles}
    >
      {!modal && <span className="popup-title">Compartilhado com</span>}
      {modal && (
        <div className='sorting-header'>
          <p className='sorting-header-title'>Pessoas com acesso</p>
          <div ref={orderButtonRef}>
            <BaSeButton
              value="Ordenar"
              rightIcon="direction"
              type="secondary"
              color="#2E5DE6"
              onClick={() => {
                setOpenOrderPopup(!openOrderPopup)
                eventTag(event?.name, "atendimento", event?.action, "Ordenar");
              }}
            />
          </div>
        </div>
      )}
      <div className={`share-details-content ${modal ? 'modal-style' : ''}`}>
        {sortedUsers.map((user, index) => (
          <div key={user.id || index} className="share-detail-item">
            <div className={`avatar ${modal ? 'modal-style' : ''}`}>
              <span>{user.name.charAt(0).toUpperCase()}</span>
            </div>
            <div className={`user-info ${modal ? 'modal-style' : ''}`}>
              <span className="user-name">{user.name}</span>
              {user.role === "Planejador" ? (
                <div className="role-badge planner">
                  <img src={lightbulb} alt="Planejador" />
                  <span>Planejador</span>
                </div>
              ) : isPlanner ? (
                <div className="role-selector">
                  <div
                    ref={el => roleTriggerRefs.current[index] = el}
                    className="role-badge"
                    onClick={() => handleRoleClick(index)}
                  >
                    <div className="role-info">
                      <BaSeIcon
                        description={user.role}
                        name={getRoleIcon(user.role)}
                        size={16}
                      />
                      <span>{user.role}</span>
                    </div>
                    <BaSeIcon
                      color="#005EB8"
                      description="Alterar"
                      name={activeRoleIndex === index ? 'angle-up' : 'angle-down'}
                      size={16}
                    />
                  </div>
                </div>
              ) : (
                <div className="role-badge role-badge--static">
                  <div className="role-info">
                    <BaSeIcon
                      description={user.role}
                      name={getRoleIcon(user.role)}
                      size={16}
                    />
                    <span>{user.role}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {openOrderPopup && (
        <div
          ref={orderPopupRef}
          className="order-popup"
          style={{
            top: window.innerWidth < 768 ? '-153px' : '50px',
          }}
        >
          <div 
            className={`order-option ${sortOrder === 'name-asc' ? 'active' : ''}`}
            onClick={() => {
              setSortOrder('name-asc');
              setOpenOrderPopup(false);
            }}
          >
            <span>Nome (A a Z)</span>
          </div>
          <div 
            className={`order-option ${sortOrder === 'name-desc' ? 'active' : ''}`}
            onClick={() => {
              setSortOrder('name-desc');
              setOpenOrderPopup(false);
            }}
          >
            <span>Nome (Z a A)</span>
          </div>
          <div 
            className={`order-option ${sortOrder === 'status-view' ? 'active' : ''}`}
            onClick={() => {
              setSortOrder('status-view');
              setOpenOrderPopup(false);
            }}
          >
            <span>Status visualização</span>
          </div>
          <div 
            className={`order-option ${sortOrder === 'status-edit' ? 'active' : ''}`}
            onClick={() => {
              setSortOrder('status-edit');
              setOpenOrderPopup(false);
            }}
          >
            <span>Status edição</span>
          </div>
        </div>
      )}

      {activeRoleIndex !== null && isPlanner && (
        <div
          ref={rolePopupRef}
          className="change-role-popup"
          style={{
            position: 'fixed',
            top: rolePopupPosition.top === 'unset' ? 'unset' : `${rolePopupPosition.top}px`,
            bottom: rolePopupPosition.bottom === 'unset' ? 'unset' : `${rolePopupPosition.bottom}px`,
            left: `${rolePopupPosition.left}px`
          }}
        >
          <div
            className="role-option"
            onClick={() => {
              handleChangeRole("Visualização", activeRoleIndex);
            }}
          >
            <BaSeIcon
              color="#2E5DE6"
              description="Visualizar"
              name="eye"
              size={20}
            />
            <span>Visualização</span>
          </div>
          <div
            className="role-option"
            onClick={() => {
              handleChangeRole("Edição", activeRoleIndex);
            }}
          >
            <BaSeIcon
              color="#2E5DE6"
              description="Editar"
              name="edit-alt"
              size={20}
            />
            <span>Edição</span>
          </div>
          <div
            className="role-option"
            onClick={() => {
              handleChangeRole("Consultor", activeRoleIndex);
            }}
          >
            <BaSeIcon
              color="#2E5DE6"
              description="Consultor"
              name="edit-alt"
              size={20}
            />
            <span>Consultor</span>
          </div>
          {isPlanner && (
            <div
              className="role-option remove-option"
              onClick={() => {
                setUserToRemove(users[activeRoleIndex]);
                setActiveRoleIndex(null);
                setShowConfirmationModal(true);
                eventTag(event?.name, "atendimento", event?.action, "Remover")
              }}
            >
              <BaSeIcon
                color="#B22400"
                description="Remover"
                name="trash-alt"
                size={20}
              />
              <span>Remover</span>
            </div>
          )}
        </div>
      )}
      {showConfirmationModal && (
        <ModalDefault
          callBackClose={() => {
            setShowConfirmationModal(false);
            setUserToRemove(null);
          }}
          show={showConfirmationModal}
          title="Remover acesso"
          children={ConfirmationModalBody()}
        />
      )}
    </div>
  );
};

export default ShareDetailsPopup;
